// ADR 25.9.26g §6 — the three events behind the web's install strip.
//
// Replaces 17.9.26k's Android invite emitter, in its shape: named constants, a
// sink seam, and `shown` latched once per tab **on delivery** with a flush
// after `initTelemetry` settles. That shape exists because web consent is
// denied by default (6.9.26c): a `shown` fired before consent resolves has to
// be parked rather than lost. Not re-exported through `telemetry/index.ts`,
// which is the consent/attach surface.
//
// THE `web_` PREFIX (6.9.26p). The strip is web-only: the phones install from
// a store, so a native client will never emit these.
//
// NO PATHNAME ON THE WIRE. `route` is the first path segment only: a plan id
// is learner data and 9.9.26m keeps it off the wire. The cut is the emitter's
// contract, not the caller's, for 9.9.26b's reason: a second call site
// inherits it.
//
// THE PLATFORM (25.9.26h §5). Every event carries `platform` beside
// `affordance`. `platform: 'other'` is normal beside `affordance: 'prompt'`
// (Opera and the other derivatives fire the prompt; the five phone makers'
// browsers have their own platforms since 15.10.26c §4), and a
// `shown` latched as `menu_instructions` may be followed by a `clicked` as
// `prompt` once the browser fires `beforeinstallprompt`.

import { attached, capture } from './capture'

export const INSTALL_STRIP_EVENTS = {
  shown: 'web_install_strip_shown',
  clicked: 'web_install_strip_clicked',
  dismissed: 'web_install_strip_dismissed',
  /** ADR 6.10.26b §5 — the handoff panel's Open link or Copy link was pressed. */
  handoffClicked: 'web_install_handoff_clicked',
} as const

/**
 * ADR 6.10.26a §4 — two more, deliberately **not** strip events: the strip's
 * panels count `web_install_strip_clicked` with no property filter, so a
 * header or door click there would inflate them. `link_opened` is latched the
 * way `shown` is: it fires on the very load an `?install=1` link opens.
 */
export const INSTALL_ENTRY_EVENTS = {
  entryClicked: 'web_install_entry_clicked',
  linkOpened: 'web_install_link_opened',
} as const

/**
 * The strip's five live affordances (`handoff` since 6.10.26b) and `pwa/installAffordance.ts`'s
 * platforms. Restated rather than imported: `telemetry/` importing from a
 * feature folder would point the dependency the wrong way
 * (`chromeTelemetry.ts` restates for the same reason).
 */
export type StripAffordance = 'prompt' | 'ios_instructions' | 'mac_instructions' | 'menu_instructions' | 'handoff'
type StripPlatform =
  | 'ios' | 'mac_safari'
  | 'chrome_desktop' | 'edge_desktop'
  | 'chrome_android' | 'firefox_android' | 'firefox_windows'
  | 'samsung_android' | 'xiaomi_android' | 'huawei_android' | 'oppo_android' | 'vivo_android'
  | 'other'

/** `pwa/handoff.ts`'s `HandoffTarget`, restated for the same reason (6.10.26b §5). */
export type HandoffTargetWire = 'windows_edge' | 'android_chrome' | 'mac_copy' | 'linux_copy'

/** `pwa/installAffordance.ts`'s `IosBrowser`, restated for the same reason. */
type StripIosBrowser = 'safari' | 'chrome' | 'edge' | 'firefox' | 'other'

/** What every strip event names: the kind of install, and the browser it was offered in. */
export interface StripContext {
  affordance: StripAffordance
  platform: StripPlatform
  /** 6.10.26a §4 — sent as `ios_browser`, and only when `platform` is `ios`. */
  iosBrowser?: StripIosBrowser
}

/** The non-strip install CTAs (6.10.26a §1, §4). */
export type EntrySurface = 'header' | 'door' | 'account'

/** `affordance`, `platform`, and `ios_browser` on iOS only — the cut is the emitter's. */
function contextProperties(strip: StripContext): Record<string, unknown> {
  const properties: Record<string, unknown> = { affordance: strip.affordance, platform: strip.platform }
  if (strip.platform === 'ios' && strip.iosBrowser !== undefined) properties.ios_browser = strip.iosBrowser
  return properties
}

/**
 * Where an event goes, and **whether it landed** — the `boolean` feeds the
 * delivery latch below, since a strip that mounts with the shell fires before
 * PostHog exists on a cold load.
 */
export type InstallStripSink = (name: string, properties: Record<string, unknown>) => boolean

/**
 * The production sink. `attached()` is 6.9.26c's own test seam and is *read*
 * here, never written: `capture()` is a no-op with no PostHog instance, and
 * that no-op is exactly what this has to be able to tell apart from a delivery.
 */
const defaultSink: InstallStripSink = (name, properties) => {
  capture(name, properties)
  return attached().analytics
}

let sink: InstallStripSink = defaultSink

/** Test seam: watch the events without attaching an SDK. */
export function setInstallStripSink(replacement: InstallStripSink): void {
  sink = replacement
}

/** Put 6.9.26c's `capture()` back — the production path. */
export function resetInstallStripSink(): void {
  sink = defaultSink
}

// Module scope, not a ref: StrictMode double-invokes effects and a
// per-component guard would make "once per tab" true in production and false
// in the suite. `pending` is a `shown` the sink could not deliver, parked for
// the flush.
let shown = false
let pending: Record<string, unknown> | null = null
let linkOpened = false
let pendingLink: Record<string, unknown> | null = null

/** Test-only: clears both latches and their parked events. */
export function resetInstallStripTelemetryState(): void {
  shown = false
  pending = null
  linkOpened = false
  pendingLink = null
}

/**
 * The first path segment, or `home` for the root.
 *
 * The leading slash dropped, then everything from the first `/`, `?` or `#`
 * on: `/plans/123` → `plans`, `/plans/` → `plans`, `/` → `home`, and a
 * `/vocab?x=1` that should never reach it (`useLocation().pathname` already
 * excludes `search` and `hash`) → `vocab`.
 */
export function routeFamily(pathname: string): string {
  const bare = pathname.startsWith('/') ? pathname.slice(1) : pathname
  const cut = bare.search(/[/?#]/)
  const family = cut === -1 ? bare : bare.slice(0, cut)
  return family === '' ? 'home' : family
}

/** Try `shown`, and latch only if it landed. */
function deliverShown(properties: Record<string, unknown>): void {
  if (sink(INSTALL_STRIP_EVENTS.shown, properties)) {
    shown = true
    pending = null
    return
  }
  pending = properties
}

/**
 * The strip was seen — once per tab, latched on delivery.
 *
 * Called from `<InstallStrip />`'s mount effect (the strip, never the slot:
 * the slot mounts on every route and returns `null` on most of them). Because
 * the latch is once per tab, `route` means "where the strip was **first**
 * seen".
 */
export function emitInstallStripShown(strip: StripContext, pathname: string): void {
  if (shown) return
  deliverShown({ ...contextProperties(strip), route: routeFamily(pathname) })
}

/**
 * The second half of the delivery latch, chained behind `initTelemetry` at the
 * composition root (`main.tsx`). A tab that denied consent settles with
 * nothing attached, so this delivers nothing and `shown` stays parked — the
 * correct reading of a denied tab.
 */
export function flushInstallStripShown(): void {
  if (shown || pending === null) return
  deliverShown(pending)
}

/** Try `link_opened`, and latch only if it landed. */
function deliverLinkOpened(properties: Record<string, unknown>): void {
  if (sink(INSTALL_ENTRY_EVENTS.linkOpened, properties)) {
    linkOpened = true
    pendingLink = null
    return
  }
  pendingLink = properties
}

/**
 * ADR 6.10.26a §4 — an `?install=1` request opened the strip. Once per tab,
 * latched on delivery like `shown`; `affordance` and `platform` only.
 */
export function emitInstallLinkOpened(strip: StripContext): void {
  if (linkOpened) return
  deliverLinkOpened({ affordance: strip.affordance, platform: strip.platform })
}

/** `flushInstallStripShown`'s twin, chained beside it in `main.tsx`. */
export function flushInstallLinkOpened(): void {
  if (linkOpened || pendingLink === null) return
  deliverLinkOpened(pendingLink)
}

// None of the four below is latched: all fire from a tap, long after init,
// so the sink's boolean is ignored.

/** The CTA was pressed; the prompt or the steps proceed beside it. */
export function emitInstallStripClicked(strip: StripContext): void {
  sink(INSTALL_STRIP_EVENTS.clicked, contextProperties(strip))
}

/** The `×` was pressed, before the dismissal is written. */
export function emitInstallStripDismissed(strip: StripContext): void {
  sink(INSTALL_STRIP_EVENTS.dismissed, contextProperties(strip))
}

/** ADR 6.10.26a §4 — a non-strip install CTA (header, door, account page) was pressed. */
export function emitInstallEntryClicked(surface: EntrySurface, strip: StripContext): void {
  sink(INSTALL_ENTRY_EVENTS.entryClicked, { surface, ...contextProperties(strip) })
}

/**
 * ADR 6.10.26b §5 — the handoff panel was used. `action` tells the Open link
 * from the copy: a Windows `copy` is the one signal that `microsoft-edge:` did
 * not do its job (§0 row 2).
 */
export function emitInstallHandoffClicked(target: HandoffTargetWire, action: 'open' | 'copy'): void {
  sink(INSTALL_STRIP_EVENTS.handoffClicked, { target, action })
}
